import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Link,
  Alert,
  styled,
  CircularProgress,
  useTheme
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { httpRequest } from '../../../services/httpRequest';

interface UserProfileData {
  fullName: string;
  email: string;
  phoneNumber: string;
  business: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  federalId: string;
  faxNumber: string;
  createDate: string;
}

interface RootState {
  user: {
    userDetails: {
      UserID?: number;
      UserRole?: string;
      [key: string]: any;
    };
  };
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  '& td, & th': {
    padding: theme.spacing(2)
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'normal',
  '&.label': {
    width: '30%',
    color: theme.palette.text.primary
  },
  '&.value': {
    color: theme.palette.text.secondary
  }
}));

const UserProfile: React.FC = () => {
  const { state: locationState, township, uuid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState<UserProfileData | null>(null);
  const theme = useTheme();

  console.log(profileData);

  // Get user ID from Redux store
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const userId = userDetails.UserID;

  const [message, setMessage] = React.useState<{
    text: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  // Fetch user profile data from API
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) {
        setMessage({
          text: 'User ID not found. Please log in again.',
          type: 'error'
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await httpRequest('GET', `/auth/vendor/${userId}`);

        // Check if the response has an error message or if the status is not success
        if (response.status !== 'success' || !response.data) {
          throw new Error(response.message || 'Failed to retrieve vendor data');
        }

        // Transform API response to match our UserProfileData interface
        // The actual data is in the "data" property of the response
        const vendorData = response.data;

        const userData = {
          fullName: `${vendorData.FirstName || ''} ${
            vendorData.LastName || ''
          }`.trim(),
          email: vendorData.EmailID || '',
          phoneNumber: vendorData.PhoneNumber || '',
          business: vendorData.Business || '',
          address1: vendorData.Address1 || '',
          address2: vendorData.Address2 || '',
          city: vendorData.City || '',
          state: vendorData.State || '',
          zipCode: vendorData.ZipCode || '',
          federalId: vendorData.FederalID || '',
          faxNumber: vendorData.FaxNumber || '',
          createDate: vendorData.InsertDate || ''
        };

        setProfileData(userData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setMessage({
          text:
            error instanceof Error
              ? error.message
              : 'Failed to load profile data. Please try again later.',
          type: 'error'
        });
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [userId]);

  const handlePasswordChange = () => {
    navigate(
      `/${locationState}/${township}/${uuid}/bid-portal/Admin/my-profile/change-password`
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Unable to load profile data. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          color: theme.palette.primary.main,
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 80,
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.primary.main
          }
        }}
      >
        My Profile
      </Typography>

      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 4, marginTop: '30px' }}>
        <Box sx={{ flex: 1, maxWidth: 800 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Personal Information
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableBody>
                <StyledTableRow>
                  <StyledTableCell className="label">Full name</StyledTableCell>
                  <StyledTableCell className="value">
                    {profileData.fullName}
                  </StyledTableCell>
                </StyledTableRow>

                <StyledTableRow>
                  <StyledTableCell className="label">
                    Email address
                  </StyledTableCell>
                  <StyledTableCell className="value">
                    {profileData.email}
                  </StyledTableCell>
                </StyledTableRow>

                <StyledTableRow>
                  <StyledTableCell className="label">
                    Phone number
                  </StyledTableCell>
                  <StyledTableCell className="value">
                    {profileData.phoneNumber || 'Not available'}
                  </StyledTableCell>
                </StyledTableRow>

                <StyledTableRow>
                  <StyledTableCell className="label">
                    Registration date
                  </StyledTableCell>
                  <StyledTableCell className="value">
                    {profileData.createDate
                      ? new Date(profileData.createDate).toLocaleDateString()
                      : 'Not available'}
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 3,
              '& > *': {
                textDecoration: 'none'
              }
            }}
          >
            <Link
              component="button"
              color="primary"
              onClick={handlePasswordChange}
            >
              Change password
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserProfile;
